import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'react-collapse';
import {
  Avatar,
  Button,
  Cushion,
  Grid,
  Image,
  Rectangle,
  Shadow,
  ShimmerTypography,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { orderBy } from 'lodash';
import { Trans } from '../../lib/i18n';
import PublishedDate from '../published-date';
import Video from '../video/dynamic';
import ReplyForm from './form';
import ReplyLoadingPlaceholder from './reply-placeholder';

const Comment = ({
  author,
  canAdminister,
  canReply,
  id,
  image,
  isDeleted,
  isDeleting,
  isRestoring,
  isSubmittingReply,
  nested,
  onDelete,
  onRestore,
  onSubmitReply,
  parentId,
  published,
  replies,
  text,
  userId,
  video,
}) => {
  const [showingReplyForm, setShowingReplyForm] = useState(false);
  const [showingReplies, setShowingReplies] = useState(replies.length > 0);
  const replyBoxRef = useRef();
  const sortedReplies = orderBy(replies, ['published'], ['asc']);
  const canDelete = canAdminister || author.id === userId;

  useEffect(() => {
    if (replyBoxRef.current) {
      replyBoxRef.current.focus();
    }
  }, [showingReplyForm]);

  const renderControls = () => (
    <>
      {replies.length > 0 && (
        <Space right="small">
          <Button
            theme="text"
            onClick={() => {
              if (showingReplies) {
                setShowingReplyForm(false);
              }
              setShowingReplies(!showingReplies);
            }}
          >
            <Typography preset="name" color="dustygrey">
              <Trans
                i18nKey={
                  showingReplies ? 'common:comment_hide_responses' : 'common:comment_show_responses'
                }
              >
                {showingReplies ? 'Hide responses' : 'Show responses'}
              </Trans>
            </Typography>
          </Button>
        </Space>
      )}
      {isDeleted && canAdminister && (
        <Space right="small">
          <Button theme="text" disabled={isRestoring} onClick={() => onRestore(id, parentId)}>
            <Typography preset="name" color="dustygrey">
              <Trans i18nKey="common:comment_restore">Restore</Trans>
            </Typography>
          </Button>
        </Space>
      )}
      {!isDeleted && canDelete && (
        <Space right="small">
          <Button theme="text" disabled={isDeleting} onClick={() => onDelete(id, parentId)}>
            <Typography preset="name" color="dustygrey">
              <Trans i18nKey="common:comment_delete">Delete</Trans>
            </Typography>
          </Button>
        </Space>
      )}
      {canReply && !isDeleted && !isDeleting && (
        <Button
          theme="text"
          onClick={() => {
            setShowingReplies(showingReplyForm ? replies.length > 0 : true);
            setShowingReplyForm(!showingReplyForm);
          }}
        >
          <Typography preset="name" color="primary">
            <Trans i18nKey="common:comment_reply">Reply</Trans>
          </Typography>
        </Button>
      )}
    </>
  );

  const showVideo = video && !(isDeleting || isRestoring) && !isDeleted;

  return (
    <Cushion top="small" bottom={nested ? 'small' : 'medium'} component="div">
      <Space bottom="small">
        <Grid
          columns={nested ? `32px 1fr` : `48px 1fr`}
          rows={nested ? null : '28px 20px auto'}
          alignItems="start"
          columnGap="small"
        >
          <div style={{ gridRow: 'span 2' }}>
            <Shadow>
              <Avatar size={nested ? 32 : 48} image={author.avatar} alt={author.name} />
            </Shadow>
          </div>
          <Typography
            component="div"
            align="left"
            preset={nested ? 'description' : 'name'}
            color={nested ? 'dustygrey' : 'black'}
            style={{ alignSelf: 'center' }}
          >
            {author.name}
          </Typography>
          <Typography component="div" align="left" preset="description" color="dustygrey">
            <PublishedDate date={published} />
          </Typography>
          <Space vertical="small">
            <Shadow shadow={nested ? 'none' : 'message'}>
              <Rectangle
                fill={nested ? 'none' : 'white'}
                radius="speechBubble"
                style={{ gridColumn: nested ? '2' : '1 / span 2' }}
              >
                {showVideo && (
                  <Rectangle
                    radius={nested ? 'rounded' : 'roundedTop'}
                    style={{ overflow: 'hidden' }}
                    component="div"
                  >
                    <Video
                      videoOptions={{
                        autoplay: false,
                        controls: true,
                        aspectRatio: '16:9',
                        poster: video.thumbnail,
                        videoUrl: video.url,
                      }}
                    />
                  </Rectangle>
                )}

                {image && !isDeleted && (
                  <Rectangle
                    radius={nested ? 'rounded' : 'roundedTop'}
                    style={{ overflow: 'hidden' }}
                  >
                    <Image src={image} alt="" preset="comment" fullWidth />
                  </Rectangle>
                )}
                <Cushion
                  vertical={nested ? 'none' : 'small'}
                  horizontal={nested ? 'none' : 'medium'}
                  component="div"
                >
                  {isDeleting || isRestoring ? (
                    <>
                      <ShimmerTypography preset="body--small" />
                      <ShimmerTypography preset="body--small" width="60%" />
                    </>
                  ) : (
                    <>
                      <Typography
                        component="div"
                        align="left"
                        preset="body--small"
                        color={isDeleted ? 'dustygrey' : 'black'}
                      >
                        {text}
                      </Typography>
                    </>
                  )}
                </Cushion>
              </Rectangle>
            </Shadow>
          </Space>
          <div style={{ gridColumn: nested ? '2' : '1 / span 2' }}>{renderControls()}</div>
        </Grid>
      </Space>

      {!nested && (
        <Shadow shadow="message">
          <Rectangle fill="white" radius="speechBubble">
            <Cushion horizontal="medium" component="div">
              <Collapse isOpened={showingReplies || isSubmittingReply}>
                <Cushion top={showingReplies ? 'small' : 'none'} component="div">
                  {sortedReplies.map((reply) => (
                    <Comment
                      key={reply.id}
                      {...reply}
                      nested
                      canAdminister={canAdminister}
                      onDelete={onDelete}
                      onRestore={onRestore}
                      onSubmitReply={onSubmitReply}
                      userId={userId}
                    />
                  ))}
                  {isSubmittingReply && <ReplyLoadingPlaceholder />}
                </Cushion>
              </Collapse>

              <Collapse isOpened={showingReplyForm}>
                {showingReplyForm && (
                  <ReplyForm
                    onSubmit={(replyText) => {
                      onSubmitReply(replyText, id);
                      setShowingReplyForm(false);
                    }}
                    inputRef={replyBoxRef}
                    isSubmitting={isSubmittingReply}
                    isReply
                  />
                )}
              </Collapse>
            </Cushion>
          </Rectangle>
        </Shadow>
      )}
    </Cushion>
  );
};

Comment.defaultProps = {
  canAdminister: false,
  canReply: false,
  image: null,
  isDeleted: false,
  isDeleting: false,
  isRestoring: false,
  isSubmittingReply: false,
  nested: false,
  parentId: null,
  replies: [],
  text: null,
  userId: null,
  video: null,
};

Comment.propTypes = {
  author: PropTypes.shape({
    id: PropTypes.number,
    avatar: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  canAdminister: PropTypes.bool,
  canReply: PropTypes.bool,
  id: PropTypes.number.isRequired,
  image: PropTypes.string,
  isDeleted: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isRestoring: PropTypes.bool,
  isSubmittingReply: PropTypes.bool,
  nested: PropTypes.bool,
  onRestore: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSubmitReply: PropTypes.func.isRequired,
  parentId: PropTypes.number,
  published: PropTypes.string.isRequired,
  replies: PropTypes.arrayOf(
    PropTypes.shape({
      author: PropTypes.shape({
        avatar: PropTypes.string,
        name: PropTypes.string,
      }).isRequired,
      id: PropTypes.number.isRequired,
      published: PropTypes.string.isRequired,
      text: PropTypes.string,
    }),
  ),
  text: PropTypes.string,
  userId: PropTypes.number,
  video: PropTypes.shape({
    thumbnail: PropTypes.string,
    url: PropTypes.string,
    video: PropTypes.string,
  }),
};

export default Comment;
